import {Component, OnInit} from '@angular/core';
import {Equipment} from "../../../models/equipment";
import {PhysicalAsset} from "../../../models/physical-asset";
import {EquipmentManagerService} from "../../../services/equipment-manager.service";
import {PhysicalAssetManagerService} from "../../../services/physical-asset-manager.service";
import {AccountManagerService} from "../../../services/account-manager-service";
import {ActivatedRoute, Router} from "@angular/router";
import {CalendarEvent, EventType, EventTypeToColor, EventTypeToString} from "../../../models/calendar-event";
import {Paginator} from "../../../models/paginator";
import {Utils} from "../../../utils";
import {EventManagerService} from "../../../services/event-manager.service";
import {
  EventLinkDialogComponent,
  EventLinkDialogData
} from "../../../components/dialogs/new-event-dialog/event-link-dialog/event-link-dialog.component";
import {UiManagerService} from "../../../services/ui-manager.service";
import {MatDialog} from "@angular/material/dialog";
import {add} from "date-fns";
import {take} from "rxjs/operators";
import {
  MaintenanceFieldWorkerRecommendation,
  MaintenanceRecommendations,
  MaintenanceRemoteExpertRecommendation
} from '../../../models/maintenance-recommendation';
import {MaintenanceManagerService} from "../../../services/maintenance-manager.service";
import {Personnel} from "../../../models/personnel";
import {Maintenance, MaintenanceStatus} from "../../../models/maintenance";
import {environment} from "@environments/environment";
import {Procedure} from "../../../models/procedure";
import {AuthoringManagerService} from "../../../services/authoring-manager.service";

@Component({
  selector: 'app-calendar-event-detail-page',
  templateUrl: './calendar-event-detail-page.component.html',
  styleUrls: ['./calendar-event-detail-page.component.scss']
})
export class CalendarEventDetailPageComponent implements OnInit {

  loading = true;
  isNew = true;
  editable = false;

  event: Partial<CalendarEvent>;

  /* Search equipment */
  equipmentId: string;
  equipmentFilter: string;
  equipments: Equipment[];
  filteredEquipments: Equipment[];

  /* Search asset */
  assetId: string;
  assetFilter: string;
  assets: PhysicalAsset[];
  filteredAssets: PhysicalAsset[];

  /* Procedure bind */
  procedureId: string;
  procedureFilter: string;
  procedures: Procedure[];
  filteredProcedures: Procedure[];

  /* Search recommendations */
  isSupportRequired: boolean;
  recommendations: MaintenanceRecommendations;
  fieldWorkerFilter: string;
  filteredFieldWorkers: MaintenanceFieldWorkerRecommendation[];
  expertFilter: string;
  filteredExperts: MaintenanceRemoteExpertRecommendation[];

  constructor(
    private equipmentManagerService: EquipmentManagerService,
    private physicalAssetManagerService: PhysicalAssetManagerService,
    public accountManagerService: AccountManagerService,
    private eventManagerService: EventManagerService,
    private maintenanceManagerService: MaintenanceManagerService,
    private authoringManagerService: AuthoringManagerService,
    private uiManagerService: UiManagerService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  async ngOnInit(): Promise<void> {

    // Get startDate and isNew flag
    const data = await Promise.all([
      this.route.paramMap.pipe(take(1)).toPromise(),
      this.route.queryParams.pipe(take(1)).toPromise(),
      this.route.data.pipe(take(1)).toPromise()
    ])
    const eventId = data[0].get('id');
    const startDate = parseInt(data[1].startDate);
    const eventType = EventType[EventType[parseInt(data[1].eventType)]];
    this.isNew = data[2].isNew;

    if (this.isNew) {

      const date = new Date(startDate);
      this.event = {
        type: eventType,
        scheduledAtDate: date,
        endAtDate: add(date, { hours: 1 })
      }

    } else {

      this.event = await this.eventManagerService.getEvent(eventId);

      // TODO nested object typization
      // Fix maintenance object
      const maintenance = new Maintenance();
      Object.assign(maintenance, this.event.maintenance);
      this.event.maintenance = maintenance;

      this.event.scheduledAtDate = new Date(this.event.scheduledAt);
      this.event.endAtDate = this.event.endAt ? new Date(this.event.endAt) : null;
      this.equipmentId = this.event.equipment.model.id;
      this.assetId = this.event.equipment.id;
      this.procedureId = this.event.maintenance.procedure?.id;

    }

    if (!this.isNew && this.event.type == EventType.PLANNED_MAINTENANCE) {

      // Create field worker and expert if not exists on planned maintenance
      this.event.maintenance.fieldWorker = this.event.maintenance.fieldWorker || new Personnel();
      this.event.maintenance.remoteExpert = this.event.maintenance.remoteExpert || new Personnel();
      this.isSupportRequired = this.event.maintenance.isSupportRequired;

      // Get recommendations
      await this.getRecommendations(false);

    }

    this.equipments = await this.equipmentManagerService.getEquipments(new Paginator<Equipment>({ignorePagination: true}));
    this.filteredEquipments = this.equipments;

    // Can edit fields only if it is a training or a planned maintenance manually created
    this.editable = [EventType.TRAINING, EventType.PLANNED_MAINTENANCE].includes(this.event.type) && this.event.endAtDate >= Utils.today;

    // Check if equipment is already defined
    if (this.equipmentId) {
      await this.getPhysicalAssets();
    } else {
      this.loading = false;
    }

  }

  async updateIsSupportRequired() {
    if (!this.isNew) {
      this.loading = true;
      await this.getRecommendations(true);
      this.loading = false;
    }
  }

  async getRecommendations(reset: boolean) {
    if (environment.features.recommenderEngine) {
      this.recommendations = await this.maintenanceManagerService.getRecommendations(this.event.maintenance);
    } else {
      this.recommendations = await this.maintenanceManagerService.getFakeRecommendations(this.isSupportRequired);
    }
    if (reset) {
      this.expertFilter = '';
      this.fieldWorkerFilter = '';
      this.event.maintenance.remoteExpert = new Personnel();
      this.event.maintenance.fieldWorker = new Personnel();
    }
    this.filterPersonnel();
  }

  filterPersonnel() {
    this.filteredFieldWorkers = this.filterFieldOperator(this.recommendations.fieldWorkers, this.fieldWorkerFilter);
    this.filteredExperts = this.filterExpert(this.recommendations.remoteExperts, this.expertFilter);
  }

  filterFieldOperator(personnel: MaintenanceFieldWorkerRecommendation[], filter: string): MaintenanceFieldWorkerRecommendation[] {
    return filter ? personnel.filter(x => (x.userSkill.user.firstName + x.userSkill.user.lastName).toLowerCase().includes(filter.toLowerCase())) : personnel;
  }

  filterExpert(personnel: MaintenanceRemoteExpertRecommendation[], filter: string): MaintenanceRemoteExpertRecommendation[] {
    return filter ? personnel.filter(x => (x.user.firstName + x.user.lastName).toLowerCase().includes(filter.toLowerCase())) : personnel;
  }

  async save(goBack: boolean): Promise<void> {

    this.loading = true;

    this.event.equipmentId = this.assetId;
    this.event.typeId = this.event.type;
    this.event.scheduledAt = this.event.scheduledAtDate.getTime();
    this.event.endAt = this.event.endAtDate?.getTime();
    this.event.createdAt = Date.now();
    this.event.baseTrainingUrl = `${window.location.protocol}//${window.location.hostname}:${window.location.port}/training/`

    // Generate or update event in DB
    if (this.isNew) {

      // Create a new event
      this.event = await this.eventManagerService.createEvent(this.event as CalendarEvent);

      // Create new maintenance if event type is planned maintenance
      if (this.event.type == EventType.PLANNED_MAINTENANCE && !this.event.maintenance) {
        let plannedMaintenance = new Maintenance();
        plannedMaintenance.scheduledAt = this.event.scheduledAt;
        plannedMaintenance.isExtraordinary = false;
        plannedMaintenance.isSupportRequired = this.isSupportRequired;
        plannedMaintenance.reason = `Planned maintenance of ${new Date(plannedMaintenance.scheduledAt).toLocaleDateString()}`;
        plannedMaintenance.equipmentId = this.event.equipment.id;
        plannedMaintenance.eventId = this.event.id;
        plannedMaintenance.procedureId = this.procedureId;
        plannedMaintenance = await this.maintenanceManagerService.createMaintenance(plannedMaintenance);
        // Update maintenance status to planned
        await this.maintenanceManagerService.setMaintenanceStatus(plannedMaintenance, MaintenanceStatus.PLANNED);

        // Refetch new event
        this.event = await this.eventManagerService.getEvent(this.event.id);
      }

    } else {
      const response = await this.eventManagerService.updateEvent(this.event.id, this.event as CalendarEvent);
      if (this.event.type != EventType.PLANNED_MAINTENANCE) {
        this.event = response;
      } else {

        // Get new recommendations
        await this.getRecommendations(false);

        // Get support required
        this.event.maintenance.isSupportRequired = this.isSupportRequired;

        // Update maintenance start date and procedure
        this.event.maintenance.scheduledAt = this.event.scheduledAt;
        this.event.maintenance.procedureId = this.procedureId;
        this.event.maintenance.scheduledAt = await this.maintenanceManagerService.updateMaintenance(this.event.maintenance.id, this.event as CalendarEvent);

        // Reset bindings
        await this.updatePersonnelBindings();

      }
    }

    // Show link dialog and autosave to clipboard
    if (this.event.type == EventType.TRAINING && this.event.trainingUrl) {

      // Open dialog
      const dialogRef = this.dialog.open(EventLinkDialogComponent, {
        width: '600px',
        data: {link: this.event.trainingUrl, accessCode: this.event.isPublic ? this.event.accessCode : null} as EventLinkDialogData
      });

      // Autosave to clipboard
      await navigator.clipboard.writeText(this.event.trainingUrl);
      this.uiManagerService.notify('Link saved to clipboard!');

      // Wait dialog to be closed
      await dialogRef.afterClosed().toPromise();

      // Go to calendar
      await this.router.navigate(['../..'], { relativeTo: this.route });

    } else {

      // Go to calendar
      if (goBack) {
        await this.router.navigate(['../..'], { relativeTo: this.route });
      }

    }

    this.loading = false;

  }

  remove() {

    this.uiManagerService.showConfirmDialog(
      'Remove event',
      'Are you sure do you want to remove selected event?',
      async () => {

        this.loading = true;

        // Remove maintenance if associated
        if (this.event.maintenance) {
          await this.maintenanceManagerService.removeMaintenance(this.event.maintenance);
        }

        // Remove event
        await this.eventManagerService.removeEvent(this.event.id);

        await this.router.navigate(['../..'], { relativeTo: this.route });

        this.loading = false;

      }
    );

  }

  hasRequiredLevel() {
    return this.recommendations.fieldWorkers.find(x => x.userSkill.user.id == this.event.maintenance.fieldWorker.id)?.hasRequiredLevel || false;
  }

  async updatePersonnelBindings() {
    await this.maintenanceManagerService.updatePersonnelBindings(
      this.event.maintenance.id,
      this.event.maintenance.fieldWorker.id,
      this.hasRequiredLevel() ? null : this.event.maintenance.remoteExpert.id,
      this.accountManagerService.account.id,
      !this.hasRequiredLevel()
    );
  }

  startDateChanged() {

    // Update training end date
    if (this.event.endAtDate < this.event.scheduledAtDate) {
      this.event.endAtDate = this.event.scheduledAtDate;
    }

    // Update planned event start date
    /*if (!this.isNew && this.event.type == EventType.PLANNED_MAINTENANCE) {
      this.save(false);
    }*/

  }

  copyToClipboard(text, sender) {

    // Get the span
    if (sender.tagName == 'BUTTON') {
      sender = sender.childNodes[0];
    }

    const textBackup = sender.textContent;
    sender.textContent = 'Copied!';
    sender.parentElement.setAttribute('disabled', 'true');
    navigator.clipboard.writeText(text);
    setTimeout(() => {
      sender.textContent = textBackup;
      sender.parentElement.removeAttribute('disabled');
    }, 1500);

  }

  async getPhysicalAssets() {

    // Check if selected equipment is valid
    if (!this.equipments.find(x => x.id == this.equipmentId)) {
      return;
    }

    this.loading = true;

    // Get assets
    this.assets = await this.physicalAssetManagerService.getPhysicalAssets(this.equipmentId, null, new Paginator<PhysicalAsset>({ignorePagination: true}));
    this.filteredAssets = this.assets;

    // Get procedures
    this.procedures = await this.authoringManagerService.getProcedures(this.equipmentId, new Paginator<Procedure>({ignorePagination: true}));
    this.filteredProcedures = this.procedures;

    this.loading = false;
  }

  isValid(form: any) {
    return form.checkValidity() && this.equipments.find(x => x.id == this.equipmentId) && this.assets.find(x => x.id == this.assetId);
  }

  async goToMaintenanceReport() {
    await this.router.navigate(['maintenance', this.event.maintenance.id]);
  }

  async goToMaintenances() {
    this.router.navigate(['maintenance']);
  }

  updateFilteredEquipments() {
    this.filteredEquipments = this.equipments.filter(x => x.name.toLowerCase().includes(this.equipmentFilter.toLowerCase()));
  }

  updateFilteredAssets() {
    this.filteredAssets = this.assets.filter(x => x.serialNumber.toLowerCase().includes(this.assetFilter.toLowerCase()));
  }

  updateFilteredProcedures() {
    this.filteredProcedures = this.procedures.filter(x => x.name.toLowerCase().includes(this.procedureFilter.toLowerCase()));
  }

  canBindPersonnel() {
    return this.event.maintenance && this.event.maintenance.status == MaintenanceStatus.PLANNED; // && this.accountManagerService.isTeamLeader();
  }

  getFieldWorker(): string {
    const fieldWorker = this.recommendations.fieldWorkers.find(x => x.userSkill.user.id == this.event.maintenance.fieldWorker.id);
    if (fieldWorker) {
      let name = `${fieldWorker.userSkill.user.lastName} ${fieldWorker.userSkill.user.firstName}`;
      if (fieldWorker.userSkill.skill && fieldWorker.userSkill.skillLevel) {
        name += ` (${fieldWorker.userSkill.skill.name} - ${fieldWorker.userSkill.skillLevel.name})`;
      }
      return name;
    }
    return '';
  }

  getRemoteExpert(): string {
    const remoteExpert = this.recommendations.remoteExperts.find(x => x.user.id == this.event.maintenance.remoteExpert.id);
    if (remoteExpert) {
      let name = `${remoteExpert.user.lastName} ${remoteExpert.user.firstName}`;
      if (remoteExpert.skill && remoteExpert.skillLevel) {
        name += ` (${remoteExpert.skill.name} - ${remoteExpert.skillLevel.name})`;
      }
      return name;
    }
    return '';
  }

  get now() {
    return new Date();
  }

  get Utils() { return Utils }
  get EventType() { return EventType; }
  get EventTypeToString() { return EventTypeToString; }
  get EventTypeToColor() { return EventTypeToColor; }
  get MaintenanceStatus() { return MaintenanceStatus; }

}
